// `/auth/reset` — Новый пароль. Source: stage-4-screens.md §5.6 + auth-otp-verification.
//
// Два источника token+email:
//   - OTP-флоу: из PasswordResetContext (reset_token от /verify-reset-code);
//   - link-fallback (D1): из ?token=...&email=... в URL.
// Submit success → АВТОЛОГИН (setToken → invalidate profileMe → /). Backend
// ревокает старые токены и выдаёт свежий. 422 invalid token → link на /forgot-password.

import { useState } from 'react';
import { Link, useLocation, useSearch } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Lock } from 'lucide-react';
import { ResetPasswordSchema, type ResetPasswordForm } from '@/api/schemas';
import { useResetPasswordMutation } from '@/api/hooks/auth';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import { applyValidationErrors, getApiMessage, isApiError, useApiErrorToast } from '@/api/error';
import { setToken } from '@/lib/auth-storage';
import { usePasswordResetContext } from '@/context/PasswordResetContext';
import { useToast } from '@/hooks/use-toast';
import { MobileFrame } from '@/components/layout/MobileFrame';
import { ScreenHeader } from '@/components/layout/ScreenHeader';
import { FormField } from '@/components/forms/FormField';
import { SubmitButton } from '@/components/forms/SubmitButton';

export function ResetPassword() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const mutation = useResetPasswordMutation();
  const queryClient = useQueryClient();
  const passwordReset = usePasswordResetContext();
  const showErrorToast = useApiErrorToast();
  const { toast } = useToast();

  // OTP-контекст приоритетнее URL (link-fallback).
  const params = new URLSearchParams(search);
  const token = passwordReset.state?.resetToken ?? params.get('token') ?? '';
  const email = passwordReset.state?.email ?? params.get('email') ?? '';

  const [invalidLink, setInvalidLink] = useState(!token || !email);
  const [invalidMessage, setInvalidMessage] = useState<string>('Ссылка повреждена или истекла.');

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(ResetPasswordSchema),
    defaultValues: { token, email, password: '', password_confirmation: '' },
  });

  const onSubmit = handleSubmit(async (form) => {
    try {
      const response = await mutation.mutateAsync(form);
      passwordReset.clear();
      if ('token' in response) {
        // Verified → автологин. Race protection (как login): await setToken ДО
        // invalidate(profileMe), иначе refetch уйдёт без Bearer → 401 → loop.
        await setToken(response.token);
        await queryClient.invalidateQueries({ queryKey: queryKeys.profileMe });
        toast({ title: 'Пароль обновлён', variant: 'success' });
        setLocation('/');
      } else {
        // Unverified (контракт §516): пароль сменён, но не логиним → на вход.
        toast({ title: 'Пароль обновлён', description: 'Подтвердите email и войдите' });
        setLocation('/signin');
      }
    } catch (err) {
      if (isApiError(err) && err.response?.status === 422) {
        const errs = err.response.data?.errors;
        if (errs && (errs.token || errs.email)) {
          setInvalidMessage(getApiMessage(err, 'Ссылка истекла или недействительна'));
          setInvalidLink(true);
          return;
        }
        const applied = applyValidationErrors<keyof ResetPasswordForm>(err, setError);
        if (applied) return;
      }
      showErrorToast(err);
    }
  });

  return (
    <MobileFrame>
      <ScreenHeader onBack={() => setLocation('/signin')} title="Назад" />

      {/* Gradient-hero + blur-blob (палитра лендинга, как на /verify-code). */}
      <div className="relative flex-1 overflow-y-auto overflow-x-hidden bg-gradient-to-b from-primary/5 to-transparent px-6 pb-8 pt-2">
        <div className="pointer-events-none absolute -right-16 -top-10 h-56 w-56 rounded-full bg-primary/10 blur-3xl" />
        <div className="relative">
          <div className="mb-7 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-surface bg-gradient-to-br from-primary to-primary/70 text-primary-foreground shadow-lg shadow-primary/25">
              <Lock className="h-8 w-8" strokeWidth={1.75} />
            </div>
            <h1 className="mb-1.5 text-hl-title font-bold leading-tight tracking-tight">Новый пароль</h1>
            <p className="text-sm text-muted-foreground">Придумайте надёжный пароль</p>
          </div>

          {invalidLink ? (
            <div className="rounded-surface border border-destructive/30 bg-destructive/5 p-5 text-center">
              <p className="mb-4 text-sm leading-relaxed">{invalidMessage}</p>
              <Link
                href="/forgot-password"
                className="inline-block text-hl-secondary font-semibold text-primary-text"
              >
                Запросить сброс заново
              </Link>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <input type="hidden" {...register('token')} />
            <FormField label="Email" type="email" value={email} readOnly disabled />
            <FormField
              label="Новый пароль"
              type="password"
              autoComplete="new-password"
              placeholder="Минимум 8 символов"
              hint="Минимум 8 символов: буквы и цифры"
              error={errors.password?.message}
              {...register('password')}
            />
            <FormField
              label="Повторите пароль"
              type="password"
              autoComplete="new-password"
              error={errors.password_confirmation?.message}
              {...register('password_confirmation')}
            />
            <SubmitButton isPending={mutation.isPending}>Сохранить пароль</SubmitButton>
          </form>
          )}
        </div>
      </div>
    </MobileFrame>
  );
}
